import { Card, CardContent, CardHeader, Divider, TextField } from "@mui/material";
import { ListItem, ListItemText, Switch } from "@mui/material";
import { useEffect, useState } from "react";
import { PasswordConfirmationDialog } from "../dialogs/PasswordConfirmationDialog";
import { CompanySetting, restoreCompanySettings, updateCompanySettings } from "../companySettingsAPI";
import { useAppDispatch } from "../../../../app/hooks";
import { showSuccess, showError } from "../../../notifications/notificationsSlice";
import { CardActionButtons } from "./CardActionButtons";
import { Localized } from "@fluent/react";
import { Help } from "../../../dialogs/HelpDialog";
import { RestoreSettingsPasswordConfirmationDialog } from "../dialogs/RestoreSettingsPasswordConfirmationDialog";


interface CompanyDocumentsSettingCardProps {
    companySettings: CompanySetting[];
    onUpdate: (updatedSettings: CompanySetting[]) => void;
}

const DOCUMENT_NUMBER_REGEX = "DOCUMENT_NUMBER_REGEX";

export const CompanyDocumentsSettingCard: React.FC<CompanyDocumentsSettingCardProps> = ({ companySettings, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const dispatch = useAppDispatch()
    const [serverError, setServerError] = useState("")
    const [documentNumberRegexError, setDocumentNumberRegexError] = useState(false)

    const orgNumberGlobalUniquenessSetting = companySettings.find(setting => setting.code === "DOCUMENT_NUMBER_GLOBAL_UNIQUENESS");
    const orgDocumentNumberRegexSetting = companySettings.find(setting => setting.code === DOCUMENT_NUMBER_REGEX);

    const [numberGlobalUniquenessSetting, setNumberGlobalUniquenessSetting] = useState<CompanySetting | undefined>(orgNumberGlobalUniquenessSetting);
    const [documentNumberRegexSetting, setDocumentNumberRegexSetting] = useState<CompanySetting | undefined>(orgDocumentNumberRegexSetting);

    const isDefault = numberGlobalUniquenessSetting?.isDefault === true && documentNumberRegexSetting?.isDefault === true;

    useEffect(() => {
        setNumberGlobalUniquenessSetting(orgNumberGlobalUniquenessSetting);
        setDocumentNumberRegexSetting(orgDocumentNumberRegexSetting);
        setDocumentNumberRegexError(false);
    }, [companySettings, isEditing]);


    const handleRestore = () => {
        setRestoreDialogOpen(true);
    };
    
    const handleClose = () => {
        setDialogOpen(false);
        setServerError("")
    };

    const handleCloseRestore = () => {
        setRestoreDialogOpen(false);
        setServerError("")
    };
    
    const handleConfirmRestore = async (password: string) => {
        if (password) {
            setSaving(true)
            setServerError("")

            try {
                const result =
                    await dispatch(restoreCompanySettings({
                        password: password,
                        settings: ["DOCUMENT_NUMBER_GLOBAL_UNIQUENESS", DOCUMENT_NUMBER_REGEX]
                    }))

                if (restoreCompanySettings.fulfilled.match(result)) {
                    const settings = result.payload as CompanySetting[];
                    onUpdate(settings);
                    setNumberGlobalUniquenessSetting(settings.find(f => f.code === "DOCUMENT_NUMBER_GLOBAL_UNIQUENESS"));
                    setDocumentNumberRegexSetting(settings.find(f => f.code === DOCUMENT_NUMBER_REGEX));
                    setRestoreDialogOpen(false);
                    setIsEditing(false);
                    setDocumentNumberRegexError(false);
                    dispatch(showSuccess("saved"))
                }
                else {
                    let errorMsg = "error"

                    if (result.payload) {
                        if (result.payload.kind === 'http') {
                            const problem = result.payload.problem
                            if (problem) {
                                errorMsg = problem.title
                            }
                        }
                    }
                    setServerError(errorMsg)
                }
            }
            catch (error) {
                dispatch(showError("error"))
            }
            finally {
                setSaving(false)
            }
        }
    }

    const handleConfirmSave = async (password: string) => {
        if (password && numberGlobalUniquenessSetting && documentNumberRegexSetting) {
            setSaving(true)
            setServerError("")

            try {
                const updatedSettings: CompanySetting[] = [numberGlobalUniquenessSetting, documentNumberRegexSetting];

                const result =
                    await dispatch(updateCompanySettings({
                        password: password,
                        settings: updatedSettings
                    }))

                if (updateCompanySettings.fulfilled.match(result)) {
                    const settings = result.payload as CompanySetting[];
                    onUpdate(settings)
                    setNumberGlobalUniquenessSetting(settings.find(f => f.code === "DOCUMENT_NUMBER_GLOBAL_UNIQUENESS"));
                    setDocumentNumberRegexSetting(settings.find(f => f.code === DOCUMENT_NUMBER_REGEX));
                    setDialogOpen(false);
                    setIsEditing(false);
                    setDocumentNumberRegexError(false);
                    dispatch(showSuccess("saved"))
                }
                else {
                    let errorMsg = "error"

                    if (result.payload) {
                        if (result.payload.kind === 'http') {
                            const problem = result.payload.problem
                            if (problem) {
                                errorMsg = problem.title
                            }
                        }
                    }
                    setServerError(errorMsg)
                }
            }
            catch (error) {
                dispatch(showError("error"))
            }
            finally {
                setSaving(false)
            }
        }
    }


    const numberGlobalUniquenessMethodUsage = () => {
        setNumberGlobalUniquenessSetting(prev => prev ? { ...prev, isActive: !prev.isActive } : undefined);
    };

    const documentNumberRegexMethodUsage = () => {
        setDocumentNumberRegexSetting(prev => prev ? { ...prev, isActive: !prev.isActive } : undefined);
        setDocumentNumberRegexError(false);
    };

    const validateDocumentNumberRegex = () => {
        const isValid = !documentNumberRegexSetting?.isActive || !!documentNumberRegexSetting?.value?.trim();
        setDocumentNumberRegexError(!isValid);
        return isValid;
    };

    const handleCancel = () => {
        setIsEditing(false);
        setDocumentNumberRegexError(false);
    };

    const handleSave = () => {
        if (!validateDocumentNumberRegex()) {
            return;
        }
        setDialogOpen(true);
    };

    return (
        <Card sx={{ maxWidth: 500, borderRadius: 2, boxShadow: 3 }}>
            <CardHeader
                title={<Localized id="company-settings-documents">
                    <span>_Dokumenty</span>
                </Localized>}
                sx={{
                    textAlign: "center",
                    bgcolor: "primary.main",
                    color: "white",
                    py: 1,
                    "& .MuiCardHeader-title": { fontSize: "1rem" },
                }}
            />
            <CardContent>
                <ListItem>
                    <ListItemText
                        primary={<>
                        <Localized id="company-settings-documents-number-global-uniqueness">
                            <span>_Globalnie unikalne numery dokumentów</span>
                        </Localized>
                        <Help id="972c4d27-a0b0-42b4-a79d-3942dacb67c0"/>
                        </>}
                    />
                    <Switch
                        checked={numberGlobalUniquenessSetting?.isActive || numberGlobalUniquenessSetting?.isFixed}
                        onChange={numberGlobalUniquenessMethodUsage}
                        disabled={!isEditing || numberGlobalUniquenessSetting?.isFixed}
                    />
                </ListItem>

                <ListItem>
                    <ListItemText
                        primary={<Localized id="company-settings-documents-document-number-regex-enabled">
                            <span>_Walidacja numeru dokumentu regex</span>
                        </Localized>}
                    />
                    <Switch
                        checked={documentNumberRegexSetting?.isActive || documentNumberRegexSetting?.isFixed}
                        onChange={documentNumberRegexMethodUsage}
                        disabled={!isEditing || documentNumberRegexSetting?.isFixed}
                    />
                </ListItem>

                <ListItem>
                    <TextField
                        label={<Localized id="company-settings-documents-document-number-regex-value">
                            <span>_Regex numeru dokumentu</span>
                        </Localized>}
                        value={documentNumberRegexSetting?.value || ""}
                        onChange={(e) => {
                            const newValue = e.target.value;
                            setDocumentNumberRegexSetting(prev => prev ? { ...prev, value: newValue } : undefined);
                            if (documentNumberRegexError && newValue.trim()) {
                                setDocumentNumberRegexError(false);
                            }
                        }}
                        onBlur={validateDocumentNumberRegex}
                        disabled={!isEditing || !documentNumberRegexSetting?.isActive}
                        variant="outlined"
                        fullWidth
                        size="small"
                        error={documentNumberRegexError}
                        helperText={documentNumberRegexError ? <Localized id="company-settings-documents-document-number-regex-required"><span>_Pole jest wymagane</span></Localized> : " "}
                    />
                </ListItem>

                <Divider sx={{ width: "100%", mt: 3 }} />
                <CardActionButtons
                    isDefault={isDefault}
                    isEditing={isEditing}
                    onRestore={handleRestore}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onEdit={() => setIsEditing(true)}
                />

            </CardContent>
            {dialogOpen &&
                <PasswordConfirmationDialog
                    saving={saving}
                    serverError={serverError}
                    onClose={handleClose}
                    onConfirm={handleConfirmSave}
                />
            }
            {restoreDialogOpen &&
                <RestoreSettingsPasswordConfirmationDialog
                    saving={saving}
                    serverError={serverError}
                    onClose={handleCloseRestore}
                    onConfirm={handleConfirmRestore}
                />
            }
        </Card>

    );
};
